

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token   = localStorage.getItem('accessToken');

export default function BorrowedPropertySection({ caseId, sofaResponse }) {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    owner_name: '',
    owner_street1: '',
    owner_street2: '',
    owner_city: '',
    owner_state: '',
    owner_zip_code: '',
    property_location_street1: '',
    property_location_street2: '',
    property_location_city: '',
    property_location_state: '',
    property_location_zip_code: '',
    property_description: '',
    value: ''
  });
  const [editItem, setEditItem] = useState(null);

  // Fetch list
  useEffect(() => {
    if (!sofaResponse) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-borrowed-properties/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch borrowed properties');
        setItems(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
  }, [caseId, sofaResponse]);

  // Open add
  function openAddModal() {
    setNewItem({
      owner_name: '',
      owner_street1: '',
      owner_street2: '',
      owner_city: '',
      owner_state: '',
      owner_zip_code: '',
      property_location_street1: '',
      property_location_street2: '',
      property_location_city: '',
      property_location_state: '',
      property_location_zip_code: '',
      property_description: '',
      value: ''
    });
    setModalOpen(true);
  }

  // Open edit
  function openEditModal(item) {
    setEditItem({ ...item });
    setEditModalOpen(true);
  }

  // Save new
  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-borrowed-properties/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newItem)
        }
      );
      if (!res.ok) throw new Error('Failed to add item');
      const created = await res.json();
      setItems(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  // Save edit
  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-borrowed-properties/${editItem.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editItem)
        }
      );
      if (!res.ok) throw new Error('Failed to update item');
      const updated = await res.json();
      setItems(prev =>
        prev.map(i => (i.id === updated.id ? updated : i))
      );
      setEditModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  // Delete
  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-borrowed-properties/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error('Failed to delete item');
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">
        Question 23: Borrowed Property
      </h2>
      <p className="text-sm text-gray-600 mb-4">
            List property that you hold, control, or store for another person.
        </p>
      {items.length === 0 ? (
        <p>No borrowed property entries found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {items.map(item => (
            <div
              key={item.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => { e.stopPropagation(); handleDelete(item.id); }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                    2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                    6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                    2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                    0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                    00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">{item.owner_name}</h3>
              <p className="mt-1 text-sm">
                {item.owner_street1}{item.owner_street2 ? `, ${item.owner_street2}` : ''}, {item.owner_city}, {item.owner_state} {item.owner_zip_code}
              </p>
              <p className="mt-2 text-sm">
                <strong>Location:</strong> {item.property_location_street1}{item.property_location_street2 ? `, ${item.property_location_street2}` : ''}, {item.property_location_city}, {item.property_location_state} {item.property_location_zip_code}
              </p>
              <p className="mt-2 text-sm"><strong>Description:</strong> {item.property_description}</p>
              <p className="mt-2 text-sm"><strong>Value:</strong> ${item.value}</p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Borrowed Property
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-xl overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Add Borrowed Property</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Owner Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newItem.owner_name}
                onChange={e => setNewItem(prev => ({ ...prev, owner_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Owner Street 1</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newItem.owner_street1}
                  onChange={e => setNewItem(prev => ({ ...prev, owner_street1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Owner Street 2</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newItem.owner_street2}
                  onChange={e => setNewItem(prev => ({ ...prev, owner_street2: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">Owner City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newItem.owner_city}
                  onChange={e => setNewItem(prev => ({ ...prev, owner_city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newItem.owner_state}
                  onChange={e => setNewItem(prev => ({ ...prev, owner_state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Owner Zip</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newItem.owner_zip_code}
                  onChange={e => setNewItem(prev => ({ ...prev, owner_zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Property Location Street 1</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newItem.property_location_street1}
                onChange={e => setNewItem(prev => ({ ...prev, property_location_street1: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Property Location Street 2</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newItem.property_location_street2}
                onChange={e => setNewItem(prev => ({ ...prev, property_location_street2: e.target.value }))}
              />
            </div>
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">Location City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newItem.property_location_city}
                  onChange={e => setNewItem(prev => ({ ...prev, property_location_city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newItem.property_location_state}
                  onChange={e => setNewItem(prev => ({ ...prev, property_location_state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Location Zip</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newItem.property_location_zip_code}
                  onChange={e => setNewItem(prev => ({ ...prev, property_location_zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={newItem.property_description}
                onChange={e => setNewItem(prev => ({ ...prev, property_description: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Value</label>
              <input
                type="number"
                step="0.01"
                className="w-full border rounded px-2 py-1"
                value={newItem.value}
                onChange={e => setNewItem(prev => ({ ...prev, value: e.target.value }))}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveNew} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Modal */}
      {editModalOpen && editItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-xl overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Edit Borrowed Property</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Owner Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editItem.owner_name}
                onChange={e => setEditItem(prev => ({ ...prev, owner_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Owner Street 1</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editItem.owner_street1}
                  onChange={e => setEditItem(prev => ({ ...prev, owner_street1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Owner Street 2</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editItem.owner_street2}
                  onChange={e => setEditItem(prev => ({ ...prev, owner_street2: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">Owner City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editItem.owner_city}
                  onChange={e => setEditItem(prev => ({ ...prev, owner_city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editItem.owner_state}
                  onChange={e => setEditItem(prev => ({ ...prev, owner_state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Owner Zip</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editItem.owner_zip_code}
                  onChange={e => setEditItem(prev => ({ ...prev, owner_zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Property Location Street 1</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editItem.property_location_street1}
                onChange={e => setEditItem(prev => ({ ...prev, property_location_street1: e.target.value }))}             
               />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Property Location Street 2</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editItem.property_location_street2}
                onChange={e => setEditItem(prev => ({ ...prev, property_location_street2: e.target.value }))}             
              />
            </div>
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">Location City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editItem.property_location_city}
                  onChange={e => setEditItem(prev => ({ ...prev, property_location_city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editItem.property_location_state}
                  onChange={e => setEditItem(prev => ({ ...prev, property_location_state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Location Zip</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editItem.property_location_zip_code}
                  onChange={e => setEditItem(prev => ({ ...prev, property_location_zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={editItem.property_description}
                onChange={e => setEditItem(prev => ({ ...prev, property_description: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Value</label>
              <input
                type="number"
                step="0.01"
                className="w-full border rounded px-2 py-1"
                value={editItem.value}
                onChange={e => setEditItem(prev => ({ ...prev, value: e.target.value }))}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}